import {
  AboveValueConfig,
  AfterDateValueConfig,
  AfterDateValueConfigKind,
  AfterFilterValueConfig,
  BeforeDateValueConfig,
  BeforeDateValueConfigKind,
  BeforeFilterValueConfig,
  BetweenDateValueConfig,
  BetweenDateValueConfigKind,
  CohortFilterGranularity,
  ExactlyValueConfig,
  InTheLastValueConfig,
  InTheLastValueConfigKind,
  LastInteractionTagFilterConfig,
} from '@/api/generated/model';
import { GenericFilter } from '@/comps/FilterPicker/FilterPicker';
import { filterToOperator, FilterOperator, getOperatorLabel } from '@/comps/FilterPicker/filter-operators';
import { FILTERS } from '@/models/common/filters';
import { formatToHuman } from '@/utils/date.util';
import { formatDuration } from '@/utils/format.util';
import { humanizeString } from '@/utils/string.utils';
import { translate } from '@/utils/translations';
import { CohortUserFilter, getCohortUserFilterLabel } from './cohort-user-filters';

type CountConfig = AboveValueConfig | BelowValueConfig | BetweenValueConfig | ExactlyValueConfig;
type BelowValueConfig = import('@/api/generated/model').BelowValueConfig;
type BetweenValueConfig = import('@/api/generated/model').BetweenValueConfig;

type DateConfig =
  | InTheLastValueConfig
  | AfterDateValueConfig
  | BeforeDateValueConfig
  | BetweenDateValueConfig
  | AfterFilterValueConfig
  | BeforeFilterValueConfig;

export function formatComparatorKind(kind: string | undefined): string {
  switch (kind) {
    case 'above':
      return 'more than';
    case 'below':
      return 'fewer than';
    case 'between':
      return 'between';
    case 'exactly':
      return 'exactly';
    default:
      return humanizeString(kind ?? 'above').toLowerCase();
  }
}

export function pluralizeGranularity(granularity: CohortFilterGranularity | undefined, count: number): string {
  const unit = granularity ?? CohortFilterGranularity.day;
  if (unit === CohortFilterGranularity.conversation) {
    return count === 1 ? 'conversation' : 'conversations';
  }
  const base = String(unit);
  if (count === 1) {
    return base;
  }
  return `${base}s`;
}

export function formatInTheLastPhrase(config: InTheLastValueConfig): string {
  const count = config.value ?? 1;
  const unit = pluralizeGranularity(config.granularity, count);
  return `in the last ${count} ${unit}`;
}

export function formatRollingWindowPhrase(config: InTheLastValueConfig): string {
  const count = config.value ?? 1;
  const unit = pluralizeGranularity(config.granularity, count);
  return `over the last ${count} ${unit}`;
}

function formatAfterBeforeFilterValue(config: AfterFilterValueConfig | BeforeFilterValueConfig): string {
  const columnLabel = translate('common', config.value.column) || humanizeString(String(config.value.column));
  const values = Array.isArray(config.value.values) ? config.value.values : [];
  const formattedValues = values
    .map((value) => {
      const translated = translate(config.value.column as keyof typeof translate, value);
      return translated === value ? humanizeString(value) : translated;
    })
    .join(', ');
  return `${columnLabel} is one of ${formattedValues}`;
}

export function formatDateConfigPhrase(config: DateConfig): string {
  if (config.kind === InTheLastValueConfigKind.in_the_last) {
    return formatInTheLastPhrase(config);
  }

  if (config.kind === AfterDateValueConfigKind.after) {
    if (typeof config.value === 'string') {
      return `after ${formatToHuman(new Date(config.value).valueOf())}`;
    }
    return `after ${formatAfterBeforeFilterValue(config as AfterFilterValueConfig)}`;
  }

  if (config.kind === BeforeDateValueConfigKind.before) {
    if (typeof config.value === 'string') {
      return `before ${formatToHuman(new Date(config.value).valueOf())}`;
    }
    return `before ${formatAfterBeforeFilterValue(config as BeforeFilterValueConfig)}`;
  }

  if (config.kind === BetweenDateValueConfigKind.between) {
    const start = formatToHuman(new Date(config.value.start).valueOf());
    const end = formatToHuman(new Date(config.value.end ?? Date.now()).valueOf());
    return `between ${start} and ${end}`;
  }

  return '';
}

export function formatCountPhrase(config: CountConfig, unit: string): string {
  const kindLabel = formatComparatorKind(config.kind);
  if (config.kind === 'between' && Array.isArray(config.value)) {
    return `${kindLabel} ${config.value[0]} and ${config.value[1]} ${unit}`;
  }
  const count = typeof config.value === 'number' ? config.value : 0;
  const unitLabel = pluralizeCountUnit(unit, count);
  return `${kindLabel} ${count} ${unitLabel}`;
}

function formatDurationSecondsPhrase(seconds: number): string {
  return formatDuration(seconds, { inputUnit: 'seconds', style: 'human' });
}

function formatDurationCountPhrase(config: CountConfig): string {
  const kindLabel = formatComparatorKind(config.kind);
  if (config.kind === 'between' && Array.isArray(config.value)) {
    return `${kindLabel} ${formatDurationSecondsPhrase(config.value[0])} and ${formatDurationSecondsPhrase(config.value[1])}`;
  }
  const seconds = typeof config.value === 'number' ? config.value : 0;
  return `${kindLabel} ${formatDurationSecondsPhrase(seconds)}`;
}

function pluralizeCountUnit(unit: string, count: number): string {
  if (count === 1) {
    if (unit === 'interactions') return 'interaction';
    if (unit === 'conversations') return 'conversation';
  }
  return unit;
}

export function formatPerGranularityPhrase(per?: CohortFilterGranularity): string {
  if (!per) return '';
  if (per === CohortFilterGranularity.conversation) return 'avg per conversation';
  return `per ${per}`;
}

function parseAdditionalFilter(additionalFilter: unknown): GenericFilter | undefined {
  if (!additionalFilter || typeof additionalFilter !== 'object') return undefined;
  const filter = additionalFilter as GenericFilter;
  if (!filter.column) return undefined;
  return {
    column: filter.column,
    values: Array.isArray(filter.values) ? filter.values : [],
    is_exclude: filter.is_exclude ?? false,
    select_all: filter.select_all,
  };
}

function formatScopeColumnLabel(column: string): string {
  const label = translate('common', column) || humanizeString(column);
  return label.toLowerCase();
}

function formatFilterOperatorForExpression(operator: FilterOperator): string {
  if (operator === 'equals') return 'eq';
  return getOperatorLabel(operator).toLowerCase();
}

export function formatAdditionalFilterPhrase(additionalFilter?: GenericFilter): string {
  if (!additionalFilter?.column) return '';

  const columnLabel = formatScopeColumnLabel(additionalFilter.column);
  if (additionalFilter.select_all) {
    return `${columnLabel} is all`;
  }

  const values = Array.isArray(additionalFilter.values) ? additionalFilter.values : [];
  if (!values.length) {
    return columnLabel;
  }

  const formattedValues = values
    .map((value) => {
      const translated = translate(additionalFilter.column as keyof typeof translate, value);
      return translated === value ? humanizeString(value) : translated;
    })
    .join(', ');

  const operatorLabel = formatFilterOperatorForExpression(filterToOperator(additionalFilter));
  return `${columnLabel} ${operatorLabel} ${formattedValues}`;
}

export function formatSpecialTimePhrase(values: unknown): string {
  if (!values || typeof values !== 'object' || !('kind' in values)) return '_';

  const config = values as DateConfig & { additional_filter?: unknown };
  const timePhrase = formatDateConfigPhrase(config);
  const additionalFilter = parseAdditionalFilter(config.additional_filter);
  const scopePhrase = additionalFilter ? formatAdditionalFilterPhrase(additionalFilter) : '';

  if (scopePhrase) {
    return `${timePhrase} AND ${scopePhrase}`;
  }

  return timePhrase || '_';
}

function formatInteractionCountPhrase(
  count: (CountConfig & { per?: CohortFilterGranularity }) | undefined,
  unit: string,
): string {
  if (!count) return '_';
  const perPhrase = formatPerGranularityPhrase('per' in count ? count.per : undefined);
  const countPhrase = formatCountPhrase(count, unit);
  return [countPhrase, perPhrase].filter(Boolean).join(' ');
}

export function formatInteractionFilterPhrase(column: string, values: unknown): string {
  if (!values || typeof values !== 'object') return '_';

  const record = values as Record<string, unknown>;

  if (column === FILTERS.usersInteraction) {
    const count = record.interactions_count as (CountConfig & { per?: CohortFilterGranularity }) | undefined;
    const dates = record.dates as InTheLastValueConfig | undefined;
    const countPart = formatInteractionCountPhrase(count, 'interactions');
    const windowPart = dates?.kind === InTheLastValueConfigKind.in_the_last ? formatRollingWindowPhrase(dates) : '';
    return [countPart, windowPart].filter(Boolean).join(' ');
  }

  if (column === FILTERS.userConversationsCount) {
    const count = record.conversations_count as CountConfig | undefined;
    const dates = record.dates as InTheLastValueConfig | undefined;
    const countPart = count ? formatCountPhrase(count, 'conversations') : '_';
    const windowPart = dates?.kind === InTheLastValueConfigKind.in_the_last ? formatRollingWindowPhrase(dates) : '';
    return [countPart, windowPart].filter(Boolean).join(' ');
  }

  if (column === FILTERS.userConversationLength) {
    const count = record.interactions_count as CountConfig | undefined;
    const dates = record.dates as InTheLastValueConfig | undefined;
    const countPart = count ? formatCountPhrase(count, 'turns per conversation') : '_';
    const windowPart = dates?.kind === InTheLastValueConfigKind.in_the_last ? formatRollingWindowPhrase(dates) : '';
    return [countPart, windowPart].filter(Boolean).join(' ');
  }

  return '_';
}

export function formatActivePeriodsPhrase(values: ActivePeriodsFilterConfig['values']): string {
  const { active_count: activeCount, window, granularity } = values;
  const unit = pluralizeGranularity(granularity, window ?? 1);
  const countLabel = formatCountPhrase(activeCount, `distinct ${unit} of the last ${window} ${unit}`);
  return countLabel;
}

export function formatAvgSessionLengthPhrase(values: AvgSessionLengthFilterConfig['values']): string {
  const { duration, dates } = values;
  const durationLabel = formatDurationCountPhrase(duration);
  const window =
    dates && typeof dates === 'object' && 'kind' in dates && dates.kind === InTheLastValueConfigKind.in_the_last
      ? formatRollingWindowPhrase(dates as InTheLastValueConfig)
      : '';
  return [`Avg session length ${durationLabel}`, window].filter(Boolean).join(' ');
}

type ActivePeriodsFilterConfig = import('@/api/generated/model').ActivePeriodsFilterConfig;
type AvgSessionLengthFilterConfig = import('@/api/generated/model').AvgSessionLengthFilterConfig;

export function formatTagPhrase(filter: LastInteractionTagFilterConfig): string {
  const attributeLabel = getCohortUserFilterLabel(FILTERS.lastInteractionTag);
  if (!filter.tag) return `${attributeLabel} _`;

  const tagKey = humanizeString(filter.tag).toLowerCase();
  if (filter.select_all) {
    return `${attributeLabel} ${tagKey} is all`;
  }

  const values = Array.isArray(filter.values) ? filter.values : [];
  const verb = filter.is_exclude ? 'is not' : 'is';
  if (!values.length) {
    return `${attributeLabel} ${tagKey} ${verb} _`;
  }

  const formattedValues = values
    .map((value) => {
      const translated = translate(filter.tag as keyof typeof translate, value);
      return translated === value ? humanizeString(value) : translated;
    })
    .join(', ');

  return `${attributeLabel} ${tagKey} ${verb} ${formattedValues}`;
}

function formatTagIncludesPhrase(filter: LastInteractionTagFilterConfig): string {
  if (!filter.tag) {
    return 'Includes users whose last interaction tag matches this condition.';
  }

  const tagKey = humanizeString(filter.tag).toLowerCase();
  if (filter.select_all) {
    return `Includes users whose last interaction ${tagKey} is all.`;
  }

  const values = Array.isArray(filter.values) ? filter.values : [];
  const verb = filter.is_exclude ? 'is not' : 'is';
  if (!values.length) {
    return `Includes users whose last interaction ${tagKey} ${verb} _.`;
  }

  const formattedValues = values
    .map((value) => {
      const translated = translate(filter.tag as keyof typeof translate, value);
      return translated === value ? humanizeString(value) : translated;
    })
    .join(', ');

  return `Includes users whose last interaction ${tagKey} ${verb} ${formattedValues}.`;
}

export function formatCohortFilterExpression(filter: CohortUserFilter): string {
  const label = getCohortUserFilterLabel(filter.column);

  if (filter.column === FILTERS.firstInteraction || filter.column === FILTERS.lastInteraction) {
    const phrase = formatSpecialTimePhrase(filter.values);
    return `${label} ${phrase}`;
  }

  if (filter.column === FILTERS.lastInteractionTag) {
    return formatTagPhrase(filter);
  }

  if (
    filter.column === FILTERS.usersInteraction ||
    filter.column === FILTERS.userConversationsCount ||
    filter.column === FILTERS.userConversationLength
  ) {
    const phrase = formatInteractionFilterPhrase(filter.column, filter.values);
    return `${label} ${phrase}`;
  }

  if (filter.column === FILTERS.activePeriods) {
    return formatActivePeriodsPhrase(filter.values);
  }

  if (filter.column === FILTERS.avgSessionLength) {
    return formatAvgSessionLengthPhrase(filter.values);
  }

  return label;
}

function formatFirstLastIncludesPhrase(column: string, values: unknown): string {
  const interactionLabel = column === FILTERS.firstInteraction ? 'first interaction' : 'last interaction';
  if (!values || typeof values !== 'object' || !('kind' in values)) {
    return `Includes users whose ${interactionLabel} matches this condition.`;
  }

  const config = values as DateConfig & { additional_filter?: unknown };
  const timePhrase = formatDateConfigPhrase(config);
  const additionalFilter = parseAdditionalFilter(config.additional_filter);
  const scopePhrase = additionalFilter ? formatAdditionalFilterPhrase(additionalFilter) : '';

  if (scopePhrase) {
    return `Includes users whose ${interactionLabel} was ${timePhrase} AND ${scopePhrase}.`;
  }

  return `Includes users whose ${interactionLabel} was ${timePhrase}.`;
}

export function formatCohortConditionIncludesUsers(filter: CohortUserFilter): string {
  if (filter.column === FILTERS.firstInteraction || filter.column === FILTERS.lastInteraction) {
    return formatFirstLastIncludesPhrase(filter.column, filter.values);
  }

  if (filter.column === FILTERS.lastInteractionTag) {
    return formatTagIncludesPhrase(filter);
  }

  if (filter.column === FILTERS.usersInteraction) {
    const phrase = formatInteractionFilterPhrase(filter.column, filter.values);
    return `Includes users with ${phrase}.`;
  }

  if (filter.column === FILTERS.userConversationsCount) {
    const phrase = formatInteractionFilterPhrase(filter.column, filter.values);
    return `Includes users with ${phrase}.`;
  }

  if (filter.column === FILTERS.userConversationLength) {
    const phrase = formatInteractionFilterPhrase(filter.column, filter.values);
    return `Includes users with ${phrase}.`;
  }

  if (filter.column === FILTERS.activePeriods) {
    const phrase = formatActivePeriodsPhrase(filter.values);
    return `Includes users active in ${phrase}.`;
  }

  if (filter.column === FILTERS.avgSessionLength) {
    const phrase = formatAvgSessionLengthPhrase(filter.values).replace(
      /^Avg session length /,
      'average conversation length ',
    );
    return `Includes users with ${phrase}.`;
  }

  const column = (filter as CohortUserFilter).column;
  return `Includes users matching ${getCohortUserFilterLabel(column)}.`;
}
